const RELOADED_AT_KEY = 'noconflict:chunk-reloaded-at';
const RELOAD_ONCE_PER_MS = 60_000;

/** True when this tab reloaded for a missing chunk within the last minute; throws when storage is blocked. */
function reloadedJustNow(): boolean {
  const at = Number(sessionStorage.getItem(RELOADED_AT_KEY));
  return Number.isFinite(at) && Date.now() - at < RELOAD_ONCE_PER_MS;
}

/**
 * Wraps the dynamic import of a lazy chunk. A deploy replaces every hashed chunk, so a tab opened before it asks
 * for a file that is gone (the server answers with the SPA fallback) and the import fails. The page is then
 * reloaded once to pick up the new build, and the promise never settles so Suspense holds its fallback until it
 * does. When the reload did not help, the browser is offline or the once-only mark cannot be kept, the error is
 * thrown as before.
 */
export async function importOrReload<T>(
  load: () => Promise<T>,
  reload: () => void = () => window.location.reload(),
): Promise<T> {
  try {
    return await load();
  } catch (error) {
    if (navigator.onLine === false) throw error;
    try {
      if (reloadedJustNow()) throw error;
      sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    } catch {
      throw error;
    }
    reload();
    return new Promise<T>(() => {});
  }
}
